/**
 * 1NOW-11136: the `--c-primary-50 … 900` scale, built from the tenant's primary.
 *
 * Every step was the raw primary (50 was white), so a `hover:bg-primary-700`
 * could never look different from `bg-primary-6000`. The scale is now a real one:
 *
 *   50 to 400   tints of the primary, 50 the palest and 400 the closest to it
 *   500, 600    the primary itself (the brand fill, `bg-primary-6000`, sits here)
 *   700 to 900  shades, darker each step
 *
 * The template used to paint its brand fills with `primary-100/200/300` (the
 * Search button, the date picker's Apply, the category pills, the contact
 * icons: hundreds of call sites, each with `text-on-primary` on top), which
 * only worked because those steps were the primary. They now read
 * `primary-brand` (`--c-primary-brand`, the primary itself, or the button
 * colour on Design C), so the tints are free to be tints. Do not paint a
 * brand fill with 100 to 400: it is pale, and the label on it is not derived
 * for a pale ground.
 *
 * 700 is a light touch (10%) so the label on a hovered button keeps its
 * contrast: on the QA tenant (#D97706, dark label) it stays 4.58:1.
 *
 * Values are the "r, g, b" strings the stylesheet reads through `rgb(var(…))`.
 */
export type PrimaryStep = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

const parse = (rgb: string): number[] | null => {
  const c = rgb.split(",").map((s) => Number(s.trim()));
  return c.length === 3 && c.every((v) => Number.isFinite(v) && v >= 0 && v <= 255) ? c : null;
};

const mix = (c: number[], to: number, t: number) =>
  c.map((v) => Math.round(v + (to - v) * t)).join(", ");

const channel = (v: number) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};
const luminance = ([r, g, b]: number[]) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

export const primaryScale = (primaryRgb: string): Record<PrimaryStep, string> => {
  const c = parse(primaryRgb);
  // Anything unreadable keeps the old behaviour: every step the colour it was given.
  if (!c) {
    return { 50: primaryRgb, 100: primaryRgb, 200: primaryRgb, 300: primaryRgb, 400: primaryRgb, 500: primaryRgb, 600: primaryRgb, 700: primaryRgb, 800: primaryRgb, 900: primaryRgb };
  }
  // Darkening black gives black, so a near-black primary's hover goes lighter instead.
  const hover = luminance(c) < 0.03 ? mix(c, 255, 0.2) : mix(c, 0, 0.1);
  return {
    50: mix(c, 255, 0.92),
    100: mix(c, 255, 0.82),
    200: mix(c, 255, 0.64),
    300: mix(c, 255, 0.46),
    400: mix(c, 255, 0.26),
    500: primaryRgb,
    600: primaryRgb,
    700: hover,
    800: mix(c, 0, 0.3),
    900: mix(c, 0, 0.46),
  };
};
